import {
  EndlessChestBand, EndlessRecord, GearItem, LevelModifiers, MilestoneView, ModesData, ModesState, ModesStateJson, PaidReward, PendingChest,
  endlessChestBand, endlessModifiers, endlessUnlocked, ironModifiers, ironMarks, ironOpen, ironRepeatMarks, ironTowers, metaSeed, payReward, sameModifiers, trialEndsAt,
} from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import type { LevelCatalog } from "./levelCatalog";

/**
 * Iron challenges and Endless mode as use cases (docs/MODES.md): which levels have an Iron challenge today, the run
 * modifiers that start one (and spend the day's try), the Endless run, what a finished run pays (the Iron star and its
 * Marks; the Endless bests), and the weekly Endless chest. The state is the `modes` save section (ModesState), saved
 * with the catalog (LevelCatalog.applyTo).
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const modesSection = registerSaveSection<ModesStateJson>("modes", asObject, (v) => v);

export function modesFromSave(save: HasSections): ModesState { return ModesState.fromJson(modesSection.read(save)); }
export function modesToSave(save: HasSections, state: ModesState): void { modesSection.write(save, state.toJson()); }

/** Seed tag of the weekly Endless chest. */
const ENDLESS_CHEST_TAG = "endlessChest";
/** Seed tags of the free-route milestones' chests (gear v2). */
const ENDLESS_MILESTONE_TAG = "endlessMilestone";
const IRON_MILESTONE_TAG = "ironMilestone";

/**
 * Which mode a run is (Daily Road's and challenge codes' own use cases are app dailyRoad.ts and challenges.ts; they are
 * named here so the level flow treats every mode alike).
 */
export type ModeKind = "iron" | "endless" | "daily" | "challenge" | "boss" | "race" | "odyssey" | null;

/** A finished Iron or Endless run, as the end screen reports it. `key` identifies the run (it counts once). */
export interface FinishedModeRun {
  readonly level: number;
  readonly won: boolean;
  readonly stagesCleared: number;
  readonly points: number;
  readonly modifiers: LevelModifiers;
  readonly key: string;
  readonly now: number;
}

/** What it earned. */
export interface ModeOutcome {
  readonly kind: ModeKind;
  /** Iron: the first win of the level (its Iron star lit). */
  readonly ironFirst: boolean;
  /** Iron: the Marks the win paid (0 for a loss or an unpaid repeat). */
  readonly ironMarks: number;
  /** Iron: the gems and Forge Essence a paid repeat win adds (gear v2 free routes; 0 otherwise). */
  readonly ironGems: number;
  readonly ironEssence: number;
  /** Endless: the run's score against the bests before it (null for Iron). */
  readonly endless: (EndlessRecord & { readonly waves: number; readonly points: number; readonly band: EndlessChestBand | null; readonly nextBand: EndlessChestBand | null }) | null;
}

/** A claimed free-route milestone (gear v2): what it paid and the gear its chest held (already banked). */
export interface MilestoneClaim { readonly view: MilestoneView; readonly paid: PaidReward; readonly gear: readonly GearItem[] }

/** A claimed weekly chest. */
export interface WeeklyChestClaim { readonly pending: PendingChest; readonly band: EndlessChestBand; readonly paid: PaidReward; readonly gear: readonly GearItem[] }

export class Modes {
  constructor(private readonly catalog: LevelCatalog) {}

  get data(): ModesData { return this.catalog.balance.modes; }
  get state(): ModesState { return this.catalog.modes; }

  // ---------------------------------------------------------------- Iron

  /** The level has its Iron challenge (cleared with all three stars). */
  ironOpen(level: number): boolean { return this.catalog.isCleared(level) && ironOpen(this.catalog.starsFor(level)); }
  /** Open and today's try not spent. */
  ironReady(level: number, now: number): boolean { return this.ironOpen(level) && !this.state.triedToday(level, now); }
  /** The level's Iron towers (archetype indexes) for this player. */
  ironTowers(level: number): number[] { return ironTowers(this.catalog.balance, level, this.catalog.ownedTowers()); }
  ironRun(level: number): LevelModifiers { return ironModifiers(this.catalog.balance, this.ironTowers(level), level); }
  /** Marks the next win of `level` pays: the first win's, else the repeat's. */
  ironMarks(level: number): number { return this.state.hasIronStar(level) ? ironRepeatMarks(this.data, level) : ironMarks(this.data, level); }
  /** Levels with an Iron challenge open. */
  ironLevels(): number[] { return Array.from({ length: this.catalog.highestLevelCleared }, (_, i) => i + 1).filter((l) => this.ironOpen(l)); }
  /** Open levels still without their Iron star. */
  ironWaiting(): number[] { return this.ironLevels().filter((l) => !this.state.hasIronStar(l)); }
  /** Whether the first Iron challenge is open (Captain Bram's hint). */
  get ironUnlocked(): boolean { return this.ironLevels().length > 0; }

  /**
   * The battle card's IRON suggestion: the highest open level without an Iron star whose try is free today, else the
   * highest open level whose try is free (repeat Marks), else null.
   */
  ironSuggestion(now: number): number | null {
    const ready = this.ironLevels().filter((l) => !this.state.triedToday(l, now));
    const fresh = ready.filter((l) => !this.state.hasIronStar(l));
    return fresh.length > 0 ? fresh[fresh.length - 1] : ready.length > 0 ? ready[ready.length - 1] : null;
  }

  /** An Iron run starts: spends today's try. False when the challenge is not open or the try was spent. */
  beginIron(level: number, now: number): boolean { return this.ironOpen(level) && this.state.spendTry(level, now); }

  // ---------------------------------------------------------------- Endless

  get endlessOpen(): boolean { return endlessUnlocked(this.data, this.catalog.highestLevelCleared); }
  get endlessUnlockAfter(): number { return this.data.endless.unlockAfterLevel; }
  /** The level whose ground an Endless run is played on. */
  get endlessLevel(): number { return this.data.endless.mapLevel; }
  endlessRun(): LevelModifiers { return endlessModifiers(this.catalog.highestLevelCleared); }
  /** When the current Endless week (Monday–Sunday UTC) ends. */
  weekEndsAt(now: number): number { this.state.rollWeek(this.data, now); return trialEndsAt(this.state.week); }
  /** The chest band this week's best reaches now, and the next one up. */
  weekBand(now: number): { band: EndlessChestBand | null; next: EndlessChestBand | null } {
    this.state.rollWeek(this.data, now);
    return this.bandsFor(this.state.weekWaves);
  }
  bandsFor(waves: number): { band: EndlessChestBand | null; next: EndlessChestBand | null } {
    return { band: endlessChestBand(this.data, waves), next: this.data.endless.weeklyChest.find((b) => b.fromWave > waves) ?? null };
  }
  /** A visit: rolls the week over and returns the chest a finished week left (null when none waits). */
  pendingChest(now: number): PendingChest | null { return this.endlessOpen ? this.state.rollWeek(this.data, now) : null; }

  /** Claims the waiting weekly chest: pays its band (a chest banked with its gear, supplies). Null when none waits. */
  claimWeekly(now: number): WeeklyChestClaim | null {
    const p = this.pendingChest(now);
    const band = p ? endlessChestBand(this.data, p.waves) : null;
    if (!p || !band) return null;
    this.state.takePending();
    const c = this.catalog;
    const paid = payReward(c.balance, band.reward, { profile: c.profile, wallet: c.economy.wallet, level: c.highestLevelCleared, seed: metaSeed(c.playerId, ENDLESS_CHEST_TAG, p.week), at: now, ref: `endless.${p.week}` });
    return { pending: p, band, paid, gear: paid.chest ? c.bankChest(paid.chest) : [] };
  }

  // ---------------------------------------------------------------- free-route milestones (gear v2, docs/GEAR_V2.md)

  /** The Iron star milestones (one-off), with their states, for the Iron screen's claim list. */
  ironMilestones(): MilestoneView[] { return this.state.ironMilestones(this.data); }

  /** This week's Endless milestones and any reached-but-unclaimed from last week, for the Endless screen's claim list. */
  endlessMilestones(now: number): MilestoneView[] { return this.endlessOpen ? this.state.endlessMilestones(this.data, now) : []; }

  /** Milestones waiting to be claimed (the hub badge). */
  claimableMilestones(now: number): number {
    return this.ironMilestones().filter((m) => m.reached && !m.claimed).length + this.endlessMilestones(now).filter((m) => m.reached && !m.claimed).length;
  }

  /** Claims an Iron star milestone: pays it (a chest banked with its gear). A string says why not. */
  claimIronMilestone(index: number, now: number): MilestoneClaim | string {
    const view = this.ironMilestones()[index];
    const m = this.state.claimIronMilestone(this.data, index);
    if (typeof m === "string") return m;
    return this.payMilestone(view, metaSeed(this.catalog.playerId, IRON_MILESTONE_TAG, index), now, `ironStars.${m.count}`);
  }

  /** Claims week `week`'s Endless milestone `index`. A string says why not. */
  claimEndlessMilestone(week: number, index: number, now: number): MilestoneClaim | string {
    const view = this.endlessMilestones(now).find((v) => v.week === week && v.index === index);
    const m = this.state.claimEndlessMilestone(this.data, now, week, index);
    if (typeof m === "string" || view === undefined) return typeof m === "string" ? m : "no such milestone";
    return this.payMilestone(view, metaSeed(this.catalog.playerId, `${ENDLESS_MILESTONE_TAG}:${week}`, index), now, `endlessWeek.${week}.${m.count}`);
  }

  private payMilestone(view: MilestoneView, seed: bigint, now: number, ref: string): MilestoneClaim {
    const c = this.catalog;
    const paid = payReward(c.balance, view.milestone.reward, { profile: c.profile, wallet: c.economy.wallet, level: c.highestLevelCleared, seed, at: now, ref });
    return { view: { ...view, claimed: true }, paid, gear: paid.chest ? c.bankChest(paid.chest) : [] };
  }

  // ---------------------------------------------------------------- runs

  /** Iron, Endless, Daily Road, a challenge code, an event mode (MP-21) or none, by the run's modifiers. */
  runKind(modifiers: LevelModifiers): ModeKind {
    return modifiers.endless ? "endless" : modifiers.iron ? "iron" : modifiers.daily ? "daily" : modifiers.challenge ? "challenge" : modifiers.event ? modifiers.event.mode : null;
  }

  /** Whether `modifiers` are this player's Iron run of `level` (the towers they would get now). */
  isIronRun(level: number, modifiers: LevelModifiers): boolean { return modifiers.iron !== undefined && sameModifiers(modifiers, this.ironRun(level)); }

  /**
   * A run ended: an Iron win lights the star and pays its Marks (the first win of the level), or the day's repeat
   * Marks; an Endless run counts toward this week's and the all-time bests. Safe to call again for the same run.
   */
  finishRun(r: FinishedModeRun): ModeOutcome {
    const kind = this.runKind(r.modifiers);
    if (kind === "iron") {
      if (!r.won) return { kind, ironFirst: false, ironMarks: 0, ironGems: 0, ironEssence: 0, endless: null };
      const done = this.state.recordIronWin(r.level, r.now, this.data.iron.repeatWinsPerDay);
      const marks = done.first ? ironMarks(this.data, r.level) : done.repeatPaid ? ironRepeatMarks(this.data, r.level) : 0;
      const ref = `iron.${r.level}.${r.key}`;
      if (marks > 0) this.catalog.economy.wallet.grant("marks", marks, "eventPlay", r.now, ref);
      // A paid repeat win also pays gems and Forge Essence (gear v2 free routes).
      const gems = done.repeatPaid ? this.data.iron.repeatGems : 0, essence = done.repeatPaid ? this.data.iron.repeatEssence : 0;
      this.catalog.economy.wallet.grantGems(gems, "event", r.now, ref);
      this.catalog.profile.forge.essence += essence;
      return { kind, ironFirst: done.first, ironMarks: marks, ironGems: gems, ironEssence: essence, endless: null };
    }
    if (kind === "endless") {
      const score = { waves: r.stagesCleared, points: r.points };
      const rec = this.state.recordEndless(this.data, r.now, r.key, score);
      const { band, next } = this.bandsFor(this.state.weekWaves);
      return { kind, ironFirst: false, ironMarks: 0, ironGems: 0, ironEssence: 0, endless: { ...rec, ...score, band, nextBand: next } };
    }
    return { kind, ironFirst: false, ironMarks: 0, ironGems: 0, ironEssence: 0, endless: null };
  }
}
